import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  isHugeNumber,
  maxHuge,
  parseHugeInput,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";
import type {
  WallAmplifierChallenge,
  WallAmplifierGainRaPropagationRewardEffect,
} from "@/features/hoverIdle/wall/wallChallengePolicy";

// The base operational chain: RA..PA. These form the gauge support chain and
// are reachable through normal play. Order is the top-to-bottom panel order.
export const HOV_AMPLIFIER_BASE_ENTRY_IDS = [
  "red",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "purple",
] as const;

// Amplification Wall (Wall 9) entries: LA (Light) / DA (Dark). They are formal
// Amplifier entries in state / save / restore / validation, but stay hidden,
// unpurchasable, MP-ineligible, and operationally locked until that Wall's
// first-clear reward is granted. They do not participate in
// the base gauge chain, so RA..PA behavior is unchanged by their presence.
export const HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_IDS = [
  "light",
  "dark",
] as const;

export const HOV_AMPLIFIER_ENTRY_IDS = [
  ...HOV_AMPLIFIER_BASE_ENTRY_IDS,
  ...HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_IDS,
] as const;

export type HovAmplifierEntryId = (typeof HOV_AMPLIFIER_ENTRY_IDS)[number];

const HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_ID_SET =
  new Set<HovAmplifierEntryId>(HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_IDS);

// Entries whose gauge participates in the support chain. Restricted to the
// base set so the chain endpoints (red feeds nothing above AP, purple receives
// from nothing) are preserved when amplification-wall entries are appended.
const HOV_AMPLIFIER_GAUGE_CHAIN_ENTRY_IDS: readonly HovAmplifierEntryId[] =
  HOV_AMPLIFIER_BASE_ENTRY_IDS;

export function isHovAmplifierEntryId(
  value: unknown,
): value is HovAmplifierEntryId {
  return (
    typeof value === "string" &&
    (HOV_AMPLIFIER_ENTRY_IDS as readonly string[]).includes(value)
  );
}

export type HovAmplifierColorKey = HovAmplifierEntryId;
export type HovAmplifierBurnState = "idle" | "draining" | "weak" | "starved";

// sourceEntryId is the AMP that actually drained into this entry's gauge.
// sourceColorKey is the visual color to display on the charged gauge: it
// follows the upper-charge chain so a yellow-derived OA charging RA shows
// yellow on RA, not orange. Falls back to the source AMP's own color when
// the source has no upstream visual chain.
export interface HovAmplifierRecentChargeSource {
  readonly sourceEntryId: HovAmplifierEntryId;
  readonly chargedEntryId: HovAmplifierEntryId;
  readonly sourceColorKey: HovAmplifierColorKey;
}

export type HovAmplifierRecentChargeSources = Partial<
  Record<HovAmplifierEntryId, HovAmplifierEntryId | null>
>;

// Per-entry timestamps (ms) of the most recent direct/self gauge fill: e.g.
// the player hovering RA to fill RA. Cleared when the entry's gauge drains
// to 0. Used purely for UI presentation; not persisted.
export type HovAmplifierRecentDirectChargeAtMs = Partial<
  Record<HovAmplifierEntryId, number>
>;

// Window after the last direct charge timestamp during which the entry is
// considered "recently directly charged" for visual presentation. Slightly
// longer than a single update tick so the visual does not flicker between
// frames.
export const HOV_AMPLIFIER_RECENT_DIRECT_CHARGE_WINDOW_MS = 500;

// How the gauge fill should be visually colored based on whether a recent
// direct/self charge and/or a recent support charge contribute to the
// current gauge value. `null` when neither contribution is active.
export type HovAmplifierRecentChargeMode =
  | "direct-only"
  | "support-only"
  | "direct-and-support";

export interface HovAmplifierGaugeVisualSegment {
  readonly amount: number;
  readonly sources: Partial<Record<HovAmplifierColorKey, number>>;
}

export type HovAmplifierGaugeVisualSegments = Partial<
  Record<HovAmplifierEntryId, readonly HovAmplifierGaugeVisualSegment[]>
>;

export interface HovAmplifierGaugeVisualCharge {
  readonly amount: number;
  readonly sourceColorKey: HovAmplifierColorKey;
}

export type HovAmplifierGaugeVisualChargeBatch = Partial<
  Record<HovAmplifierEntryId, readonly HovAmplifierGaugeVisualCharge[]>
>;

export const HOV_AMPLIFIER_INITIAL_AP_EXPONENT = 0.5;
export const HOV_AMPLIFIER_BASE_FILL_SECONDS = 10;
export const HOV_AMPLIFIER_BASE_DRAIN_SECONDS = 3600;
export const HOV_AMPLIFIER_BASE_FILL_RATE_PER_SECOND =
  1 / HOV_AMPLIFIER_BASE_FILL_SECONDS;
export const HOV_AMPLIFIER_BASE_DRAIN_RATE_PER_SECOND =
  1 / HOV_AMPLIFIER_BASE_DRAIN_SECONDS;
export const HOV_AMPLIFIER_GAIN_INCREMENT_PER_LEVEL = 0.1;
export const HOV_AMPLIFIER_UPGRADE_COST_MULTIPLIER_PER_LEVEL = 10;
// Fraction of an upper entry's actual gauge drain that is added to the
// gauge of the entry it feeds (e.g. OA drain → RA gauge). Tunable; keep the
// units 1:1 with the upper entry's drained gauge fraction so passive support
// stays proportional to actual consumption.
export const HOV_AMPLIFIER_LOWER_GAUGE_CHARGE_RATIO = 10;
const HOV_AMPLIFIER_MAX_VISUAL_SEGMENTS_PER_ENTRY = 24;
const HOV_AMPLIFIER_VISUAL_SEGMENT_EPSILON = 0.000001;

export interface HovAmplifierEntryDefinition {
  readonly id: HovAmplifierEntryId;
  readonly index: number;
  readonly colorKey: HovAmplifierColorKey;
  readonly labelKey: string;
  readonly feedsGaugeForEntryId: HovAmplifierEntryId | null;
  readonly receivesGaugeFromEntryId: HovAmplifierEntryId | null;
  readonly unlockedByDefault: boolean;
  readonly baseFillRatePerSecond: number;
  readonly baseDrainRatePerSecond: number;
  readonly baseCost: HugeNumber;
  readonly initialEnhancementLevel: number;
  // True for amplification-wall (Wall 9) entries that stay hidden,
  // unpurchasable, and MP-ineligible until that wall's first-clear reward.
  readonly requiresAmplificationWallClear: boolean;
}

export interface HovAmplifierState {
  readonly ap: HugeNumber;
  readonly apExponent: number;
  readonly entries: readonly HovAmplifierEntryState[];
  // cumulativeBoosts[id] is the cumulative boost on that entry's source Gain.
  // For example, cumulativeBoosts.red is `cumulativeRagBoost`, applied as
  // RAG = (1 + cumulativeRagBoost) * (RAGU count * baseRagPerUpgrade). It is
  // increased by the drain of the entry one position below (e.g. OA drain
  // feeds cumulativeRagBoost), so RA only feeds AP and the last entry's
  // boost has no source. Lives alongside AP in the OHov-cycle state.
  readonly cumulativeBoosts: Record<HovAmplifierEntryId, HugeNumber>;
}

export interface HovAmplifierGaugeDrainResult {
  readonly state: HovAmplifierState;
  readonly actualDrain: number;
}

export interface HovAmplifierEntryState {
  readonly id: HovAmplifierEntryId;
  readonly gaugeValue: number;
  readonly enhancementLevel: number;
}

export type HovAmplifierDrainTarget =
  | { readonly kind: "ap" }
  | { readonly kind: "cumulativeBoost"; readonly entryId: HovAmplifierEntryId };

export interface HovAmplifierApExponentBreakdown {
  // Base exponent stored on HovAmplifierState. Currently constant `0.5`.
  readonly base: number;
  // Fixed additive bonus from the AP Boar skill.
  readonly apBoarExponentBonus: number;
  // Dynamic per-unlocked-amplifier-kind bonus from the AP Deer skill.
  readonly apDeerExponentBonus: number;
  // Dynamic AP-dependent bonus from the AP Butterfly skill.
  readonly apButterflyExponentBonus: number;
  // base + (sum of all bonuses). Used as the exponent for AP^exp in the
  // HG multiplier formula.
  readonly effective: number;
}

export interface HovAmplifierSnapshot {
  readonly ap: HugeNumber;
  readonly apExponent: number;
  readonly hgMultiplier: HugeNumber;
  readonly apLabel: string;
  readonly hgMultiplierLabel: string;
  readonly availableHovCore: HugeNumber;
  readonly entries: readonly HovAmplifierEntrySnapshot[];
  readonly effectiveApExponent: number;
  readonly apExponentBreakdown: HovAmplifierApExponentBreakdown;
}

export const HOV_AMPLIFIER_SHORT_LABELS: Record<HovAmplifierEntryId, string> = {
  red: "RA",
  orange: "OA",
  yellow: "YA",
  green: "GA",
  cyan: "CA",
  blue: "BA",
  purple: "PA",
  light: "LA",
  dark: "DA",
};

export const HOV_AMPLIFIER_GAIN_UPGRADE_SHORT_LABELS: Record<
  HovAmplifierEntryId,
  string
> = {
  red: "RAG",
  orange: "OAG",
  yellow: "YAG",
  green: "GAG",
  cyan: "CAG",
  blue: "BAG",
  purple: "PAG",
  light: "LAG",
  dark: "DAG",
};

export interface HovAmplifierEntrySnapshot {
  readonly id: HovAmplifierEntryId;
  readonly index: number;
  readonly colorKey: HovAmplifierColorKey;
  readonly labelKey: string;
  readonly shortLabel: string;
  readonly gainUpgradeShortLabel: string;
  readonly feedsGaugeForEntryId: HovAmplifierEntryId | null;
  readonly receivesGaugeFromEntryId: HovAmplifierEntryId | null;
  readonly recentChargeSource: HovAmplifierRecentChargeSource | null;
  readonly recentChargeMode: HovAmplifierRecentChargeMode | null;
  readonly visualSegments: readonly HovAmplifierGaugeVisualSegment[];
  readonly consumingVisualSource: HovAmplifierColorKey | null;
  // Whether this entry should appear in the player-facing Amplifier panel.
  // Independent of `unlocked` (operational state): amplification-wall entries
  // (LA / DA) stay invisible until the wall reward, even if made operational.
  readonly playerVisible: boolean;
  // True only for the Wall9 challenge projection. This does not change the
  // entry's normal visibility, unlock, purchase, MP, gauge, or persisted state.
  readonly corruptedPresentation: boolean;
  readonly mpAssignable: boolean;
  readonly unlocked: boolean;
  readonly gaugeValue: number;
  readonly fillRatePerSecond: number;
  readonly drainRatePerSecond: number;
  // Charge fed into this entry's gauge per second from the upper entry's
  // gross drain (see HOV_AMPLIFIER_LOWER_GAUGE_CHARGE_RATIO). 0 when the
  // upper entry is locked or its gauge is empty.
  readonly incomingChargeRatePerSecond: number;
  // grossDrain - incomingCharge. Drives the remaining-time display so a
  // gauge that is held up by an upper entry shows --:-- instead of a
  // misleading finite duration.
  readonly netDrainRatePerSecond: number;
  readonly remainingSeconds: number | null;
  readonly isDraining: boolean;
  readonly isStarved: boolean;
  readonly isWeakBurn: boolean;
  readonly burnState: HovAmplifierBurnState;
  readonly enhancementLevel: number;
  readonly amplifierGainPerSecond: HugeNumber;
  readonly amplifierGainPerSecondAfterUpgrade: HugeNumber;
  readonly amplifierGainMultiplier: HugeNumber;
  readonly challengeSourceGainMultiplier: number;
  readonly cumulativeBoost: HugeNumber;
  // The amount this entry's source Gain produces per second after the
  // cumulative boost is applied. RAG / OAG / YAG ... live here.
  readonly effectiveAmplifierGainPerSecond: HugeNumber;
  readonly effectiveAmplifierGainPerSecondAfterUpgrade: HugeNumber;
  // The effective output the gauge is currently producing per second for the
  // gauge inner text. Equals effectiveAmplifierGainPerSecond while the gauge
  // can actually drain into its target; zero when locked, empty, or otherwise
  // unable to consume. Decoupled from the potential source Gain so View does
  // not have to infer active output from gaugeValue.
  readonly activeGaugeOutputGain: HugeNumber;
  // What the gauge drain feeds. RA drains into AP; later entries drain into
  // the cumulative boost on the entry one position above. The effective gauge
  // label (e.g. APG / RAGG / OAGG) follows the same routing.
  readonly drainTarget: HovAmplifierDrainTarget;
  readonly gaugeOutputLabel: string;
  readonly upgradeCost: HugeNumber;
  readonly canPurchaseUpgrade: boolean;
  readonly missingHovCoreForUpgrade: HugeNumber;
}

export const HOV_AMPLIFIER_ENTRY_DEFINITIONS: readonly HovAmplifierEntryDefinition[] =
  HOV_AMPLIFIER_ENTRY_IDS.map((id, index) => {
    const chainIndex = HOV_AMPLIFIER_GAUGE_CHAIN_ENTRY_IDS.indexOf(id);
    const isChained = chainIndex >= 0;
    return {
      id,
      index,
      colorKey: id,
      labelKey: `hoverIdle.hov.amplifier.entries.${id}`,
      feedsGaugeForEntryId:
        isChained && chainIndex > 0
          ? HOV_AMPLIFIER_GAUGE_CHAIN_ENTRY_IDS[chainIndex - 1]
          : null,
      receivesGaugeFromEntryId:
        isChained && chainIndex < HOV_AMPLIFIER_GAUGE_CHAIN_ENTRY_IDS.length - 1
          ? HOV_AMPLIFIER_GAUGE_CHAIN_ENTRY_IDS[chainIndex + 1]
          : null,
      unlockedByDefault: index === 0,
      baseFillRatePerSecond: HOV_AMPLIFIER_BASE_FILL_RATE_PER_SECOND,
      baseDrainRatePerSecond: HOV_AMPLIFIER_BASE_DRAIN_RATE_PER_SECOND,
      baseCost: resolveHovAmplifierEntryBaseCost(index),
      initialEnhancementLevel: index === 0 ? 1 : 0,
      requiresAmplificationWallClear:
        HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_ID_SET.has(id),
    };
  });

export function resolveHovAmplifierEntryBaseCost(index: number): HugeNumber {
  return huge(10).pow(Math.pow(2, index) - 1);
}

// Player-facing availability of an entry. Kept distinct from operational
// `unlocked` (enhancementLevel > 0): an amplification-wall entry can be made
// operational (e.g. via admin) yet remain hidden / unpurchasable until the
// Wall reward is granted. The Amplification Wall first-clear reward flips
// `amplificationWallCleared`; until then these gates stay false for
// LA / DA and true for the base RA..PA set.
export interface HovAmplifierEntryAvailabilityContext {
  readonly amplificationWallCleared?: boolean;
}

export interface HovAmplifierChallengeContext {
  readonly challenge: WallAmplifierChallenge | null;
  readonly availability?: HovAmplifierEntryAvailabilityContext;
}

export interface HovAmplifierChallengeState {
  readonly corruptedEntryIds: ReadonlySet<HovAmplifierEntryId>;
  readonly sourceGainMultipliersByEntry: Readonly<
    Record<HovAmplifierEntryId, number>
  >;
}

function isAmplificationWallEntryAvailable(
  id: HovAmplifierEntryId,
  context: HovAmplifierEntryAvailabilityContext | undefined,
): boolean {
  if (!HOV_AMPLIFIER_AMPLIFICATION_WALL_ENTRY_ID_SET.has(id)) return true;
  return context?.amplificationWallCleared === true;
}

export function isHovAmplifierEntryPlayerVisible(
  id: HovAmplifierEntryId,
  context?: HovAmplifierEntryAvailabilityContext,
): boolean {
  return isAmplificationWallEntryAvailable(id, context);
}

export function isHovAmplifierEntryPurchasable(
  id: HovAmplifierEntryId,
  context?: HovAmplifierEntryAvailabilityContext,
): boolean {
  return isAmplificationWallEntryAvailable(id, context);
}

export function isHovAmplifierEntryMpAssignable(
  id: HovAmplifierEntryId,
  context?: HovAmplifierEntryAvailabilityContext,
): boolean {
  return isAmplificationWallEntryAvailable(id, context);
}

export function resolveHovAmplifierChallengeState(
  state: HovAmplifierState,
  context: HovAmplifierChallengeContext,
): HovAmplifierChallengeState {
  const corruptedEntryIds = new Set<HovAmplifierEntryId>();
  if (context.challenge?.kind === "corrupted-unavailable-amplifiers") {
    for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
      const operationallyUnlocked = isHovAmplifierEntryUnlocked(
        state,
        definition.id,
      );
      const playerFacingAvailable = isHovAmplifierEntryPlayerVisible(
        definition.id,
        context.availability,
      );
      if (!operationallyUnlocked || !playerFacingAvailable) {
        corruptedEntryIds.add(definition.id);
      }
    }
  }

  const perCorruptedMultiplier =
    context.challenge?.lowerSourceGainMultiplierPerCorruptedEntry ?? 1;
  const sourceGainMultipliersByEntry = Object.fromEntries(
    HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) => {
      let higherCorruptedEntryCount = 0;
      for (const higherDefinition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
        if (higherDefinition.index <= definition.index) continue;
        if (corruptedEntryIds.has(higherDefinition.id)) {
          higherCorruptedEntryCount += 1;
        }
      }
      return [
        definition.id,
        perCorruptedMultiplier ** higherCorruptedEntryCount,
      ];
    }),
  ) as Record<HovAmplifierEntryId, number>;

  return { corruptedEntryIds, sourceGainMultipliersByEntry };
}

export function applyHovAmplifierRaPropagationReward(
  preWallRewardMultipliersByEntry: Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  >,
  effect: WallAmplifierGainRaPropagationRewardEffect,
): Partial<Record<HovAmplifierEntryId, HugeNumber>> {
  const result = { ...preWallRewardMultipliersByEntry };
  if (!effect.acquired) {
    return result;
  }

  const raExternalMultiplier = resolveHovAmplifierEntryGainMultiplier(
    preWallRewardMultipliersByEntry,
    "red",
  );
  const raBonus = raExternalMultiplier.sub(1);
  if (compareHuge(raBonus, 0) <= 0) return result;

  const propagatedRaMultiplier = hugeOne().add(raBonus.mul(effect.value));
  for (const entryId of HOV_AMPLIFIER_ENTRY_IDS) {
    if (entryId === "red") continue;
    const preWallEntryMultiplier = resolveHovAmplifierEntryGainMultiplier(
      preWallRewardMultipliersByEntry,
      entryId,
    );
    result[entryId] = preWallEntryMultiplier.mul(propagatedRaMultiplier);
  }
  return result;
}

export function resolveHovAmplifierGainPerSecond(
  enhancementLevel: number,
): HugeNumber {
  return huge(normalizeEnhancementLevel(enhancementLevel)).mul(
    HOV_AMPLIFIER_GAIN_INCREMENT_PER_LEVEL,
  );
}

export function resolveHovAmplifierEffectiveGainPerSecond(
  gainPerSecond: HugeNumber,
  multiplier: HugeNumber,
): HugeNumber {
  return gainPerSecond.mul(multiplier);
}

export function resolveHovAmplifierUpgradeCost(
  baseCost: HugeNumber,
  enhancementLevel: number,
): HugeNumber {
  return baseCost.mul(
    huge(HOV_AMPLIFIER_UPGRADE_COST_MULTIPLIER_PER_LEVEL).pow(
      normalizeEnhancementLevel(enhancementLevel),
    ),
  );
}

export function resolveHovAmplifierDrainTargetGainPerGauge(
  effectiveGainPerSecond: HugeNumber,
  drainRatePerSecond: number,
): HugeNumber {
  if (!Number.isFinite(drainRatePerSecond) || drainRatePerSecond <= 0) {
    return hugeZero();
  }
  return effectiveGainPerSecond.div(drainRatePerSecond);
}

export function resolveHovAmplifierCumulativeBoost(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): HugeNumber {
  const boost = state.cumulativeBoosts[entryId];
  if (!isHugeNumber(boost) || !boost.isFinite() || compareHuge(boost, 0) < 0) {
    return hugeZero();
  }
  return boost;
}

export function resolveHovAmplifierGainMultiplier(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): HugeNumber {
  return hugeOne().add(resolveHovAmplifierCumulativeBoost(state, entryId));
}

export function resolveHovAmplifierEntryGainMultiplier(
  amplifierGainMultipliersByEntry:
    | Partial<Record<HovAmplifierEntryId, HugeNumber>>
    | undefined,
  entryId: HovAmplifierEntryId,
): HugeNumber {
  const value = amplifierGainMultipliersByEntry?.[entryId];
  if (value === undefined || !isHugeNumber(value) || !value.isFinite()) {
    return hugeOne();
  }
  if (compareHuge(value, 1) <= 0) return hugeOne();
  return value;
}

export function resolveHovAmplifierDrainTarget(
  entryId: HovAmplifierEntryId,
): HovAmplifierDrainTarget {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  const target = definition?.feedsGaugeForEntryId ?? null;
  if (target === null) return { kind: "ap" };
  return { kind: "cumulativeBoost", entryId: target };
}

export function createInitialHovAmplifierCumulativeBoosts(): Record<
  HovAmplifierEntryId,
  HugeNumber
> {
  return HOV_AMPLIFIER_ENTRY_IDS.reduce(
    (acc, id) => {
      acc[id] = hugeZero();
      return acc;
    },
    {} as Record<HovAmplifierEntryId, HugeNumber>,
  );
}

export function createEmptyHovAmplifierRecentChargeSources(): Record<
  HovAmplifierEntryId,
  HovAmplifierEntryId | null
> {
  return HOV_AMPLIFIER_ENTRY_IDS.reduce(
    (acc, id) => {
      acc[id] = null;
      return acc;
    },
    {} as Record<HovAmplifierEntryId, HovAmplifierEntryId | null>,
  );
}

export function createEmptyHovAmplifierRecentDirectChargeAtMs(): HovAmplifierRecentDirectChargeAtMs {
  return {};
}

export function createEmptyHovAmplifierGaugeVisualSegments(): HovAmplifierGaugeVisualSegments {
  return {};
}

export function createInitialHovAmplifierState(): HovAmplifierState {
  return {
    ap: hugeOne(),
    apExponent: HOV_AMPLIFIER_INITIAL_AP_EXPONENT,
    entries: HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) =>
      createInitialEntryState(definition.id),
    ),
    cumulativeBoosts: createInitialHovAmplifierCumulativeBoosts(),
  };
}

export function normalizeHovAmplifierState(value: unknown): HovAmplifierState {
  const source = isPlainRecord(value) ? value : {};
  const sourceEntries = Array.isArray(source.entries) ? source.entries : [];

  const entries = HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) => {
    const sourceEntry = sourceEntries.find(
      (candidate): candidate is Record<string, unknown> =>
        isPlainRecord(candidate) && candidate.id === definition.id,
    );
    return normalizeHovAmplifierEntryState(definition, sourceEntry);
  });

  return {
    ap: normalizeHovAmplifierAp(source.ap),
    apExponent: normalizeHovAmplifierApExponent(source.apExponent),
    entries,
    cumulativeBoosts: normalizeHovAmplifierCumulativeBoosts(
      source.cumulativeBoosts,
    ),
  };
}

export function resetHovAmplifierForOHov(
  state: HovAmplifierState,
): HovAmplifierState {
  return {
    ...state,
    ap: hugeOne(),
    entries: HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) => {
      const current = findEntryState(state.entries, definition.id);
      return {
        id: definition.id,
        gaugeValue: 0,
        enhancementLevel: Math.max(
          normalizeEnhancementLevel(current?.enhancementLevel),
          definition.initialEnhancementLevel,
        ),
      };
    }),
    cumulativeBoosts: createInitialHovAmplifierCumulativeBoosts(),
  };
}

export function applyHovAmplifierCumulativeBoostGain(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
  delta: HugeNumberInput,
): HovAmplifierState {
  const deltaHuge = huge(delta);
  if (!deltaHuge.isFinite() || compareHuge(deltaHuge, 0) <= 0) return state;
  const current = resolveHovAmplifierCumulativeBoost(state, entryId);
  return {
    ...state,
    cumulativeBoosts: {
      ...state.cumulativeBoosts,
      [entryId]: current.add(deltaHuge),
    },
  };
}

export interface HovAmplifierSnapshotContext {
  readonly availableHovCore?: HugeNumber;
  readonly recentChargeSources?: HovAmplifierRecentChargeSources;
  readonly recentDirectChargeAtMs?: HovAmplifierRecentDirectChargeAtMs;
  readonly visualSegments?: HovAmplifierGaugeVisualSegments;
  // Wall-clock value (ms) used together with `recentDirectChargeAtMs` to
  // judge whether a direct charge happened recently. Optional so callers
  // that do not track direct-charge presentation can omit it.
  readonly nowMs?: number;
  // Per-entry source-Amplifier-Gain multipliers applied on top of
  // (1 + cumulativeBoost). External systems (skills, achievements, ...)
  // compose multipliers per entry; the snapshot multiplies the entry's value
  // into `effectiveAmplifierGainPerSecond`. Missing entries default to x1.
  readonly amplifierGainMultipliersByEntry?: Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  >;
  readonly availability?: HovAmplifierEntryAvailabilityContext;
  readonly amplifierChallenge?: WallAmplifierChallenge | null;
  // Effective AP exponent breakdown composed by the model from the base
  // exponent and the AP Boar / AP Deer / AP Butterfly skill bonuses. When
  // omitted, the snapshot falls back to the stored `state.apExponent` with
  // zero bonuses.
  readonly apExponentBreakdown?: HovAmplifierApExponentBreakdown;
}

export function createHovAmplifierSnapshot(
  state: HovAmplifierState,
  context: HovAmplifierSnapshotContext = {},
): HovAmplifierSnapshot {
  const apExponentBreakdown = resolveSnapshotApExponentBreakdown(
    state.apExponent,
    context.apExponentBreakdown,
  );
  const effectiveApExponent = apExponentBreakdown.effective;
  const hgMultiplier = resolveHovAmplifierHgMultiplier(
    state.ap,
    effectiveApExponent,
  );
  const availableHovCore =
    context.availableHovCore !== undefined
      ? context.availableHovCore
      : hugeZero();
  const challengeState = resolveHovAmplifierChallengeState(state, {
    challenge: context.amplifierChallenge ?? null,
    availability: context.availability,
  });

  return {
    ap: state.ap,
    apExponent: state.apExponent,
    hgMultiplier,
    apLabel: `AP ${formatForContext(state.ap, "main")}`,
    hgMultiplierLabel: `x${formatForContext(hgMultiplier, "main")} HG`,
    availableHovCore,
    entries: HOV_AMPLIFIER_ENTRY_DEFINITIONS.map((definition) =>
      createEntrySnapshot(
        definition,
        state.entries,
        state,
        availableHovCore,
        context.recentChargeSources,
        context.recentDirectChargeAtMs,
        context.visualSegments,
        context.nowMs,
        context.amplifierGainMultipliersByEntry,
        context.availability,
        challengeState,
      ),
    ),
    effectiveApExponent,
    apExponentBreakdown,
  };
}

function resolveSnapshotApExponentBreakdown(
  baseApExponent: number,
  override: HovAmplifierApExponentBreakdown | undefined,
): HovAmplifierApExponentBreakdown {
  if (override !== undefined) {
    return override;
  }
  return {
    base: baseApExponent,
    apBoarExponentBonus: 0,
    apDeerExponentBonus: 0,
    apButterflyExponentBonus: 0,
    effective: baseApExponent,
  };
}

export function resolveHovAmplifierHgMultiplier(
  ap: HugeNumber,
  exponent: number,
): HugeNumber {
  if (!Number.isFinite(exponent) || exponent < 0) return hugeOne();
  if (!ap.isFinite() || compareHuge(ap, 0) < 0) return hugeOne();

  return ap.pow(exponent);
}

export function isHovAmplifierEntryUnlocked(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): boolean {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return false;
  if (definition.unlockedByDefault) return true;
  const entry = findEntryState(state.entries, entryId);
  return normalizeEnhancementLevel(entry?.enhancementLevel) > 0;
}

export function increaseHovAmplifierGauge(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
  deltaSeconds: number,
): HovAmplifierState {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return state;
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return state;
  if (!isHovAmplifierEntryUnlocked(state, entryId)) return state;

  let mutated = false;
  const entries = state.entries.map((entry) => {
    if (entry.id !== entryId) return entry;
    const nextGauge = clamp01(
      entry.gaugeValue + definition.baseFillRatePerSecond * deltaSeconds,
    );
    if (nextGauge === entry.gaugeValue) return entry;
    mutated = true;
    return { ...entry, gaugeValue: nextGauge };
  });

  if (!mutated) return state;
  return { ...state, entries };
}

export function drainHovAmplifierGauge(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
  deltaSeconds: number,
): HovAmplifierGaugeDrainResult {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    return { state, actualDrain: 0 };
  }
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return { state, actualDrain: 0 };
  if (!isHovAmplifierEntryUnlocked(state, entryId)) {
    return { state, actualDrain: 0 };
  }

  const entry = findEntryState(state.entries, entryId);
  if (!entry) return { state, actualDrain: 0 };

  const currentGauge = clamp01(entry.gaugeValue);
  if (currentGauge <= 0) return { state, actualDrain: 0 };

  const desiredDrain = definition.baseDrainRatePerSecond * deltaSeconds;
  if (!Number.isFinite(desiredDrain) || desiredDrain <= 0) {
    return { state, actualDrain: 0 };
  }

  const actualDrain = Math.min(currentGauge, desiredDrain);
  if (actualDrain <= 0) return { state, actualDrain: 0 };

  const nextGauge = clamp01(currentGauge - actualDrain);
  const entries = state.entries.map((candidate) =>
    candidate.id === entryId
      ? { ...candidate, gaugeValue: nextGauge }
      : candidate,
  );

  return {
    state: { ...state, entries },
    actualDrain,
  };
}

export function chargeHovAmplifierLowerGaugeFromDrain(
  state: HovAmplifierState,
  sourceEntryId: HovAmplifierEntryId,
  actualDrain: number,
): HovAmplifierState {
  if (!Number.isFinite(actualDrain) || actualDrain <= 0) return state;
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === sourceEntryId,
  );
  if (!definition) return state;
  const targetId = definition.feedsGaugeForEntryId;
  if (targetId === null) return state;
  if (!isHovAmplifierEntryUnlocked(state, targetId)) return state;

  const charge = actualDrain * HOV_AMPLIFIER_LOWER_GAUGE_CHARGE_RATIO;
  if (!Number.isFinite(charge) || charge <= 0) return state;

  let mutated = false;
  const entries = state.entries.map((entry) => {
    if (entry.id !== targetId) return entry;
    const nextGauge = clamp01(entry.gaugeValue + charge);
    if (nextGauge === entry.gaugeValue) return entry;
    mutated = true;
    return { ...entry, gaugeValue: nextGauge };
  });
  if (!mutated) return state;
  return { ...state, entries };
}

// New segments are added on the "newest" side (index 0 / left) so that
// `visualSegments` reads left→right as newest→oldest. Consumption pops from
// the rightmost slot (`segments[length - 1]`).
export function addHovAmplifierGaugeVisualCharges(
  segmentsByEntry: HovAmplifierGaugeVisualSegments,
  batch: HovAmplifierGaugeVisualChargeBatch,
): HovAmplifierGaugeVisualSegments {
  let next = segmentsByEntry;
  for (const id of HOV_AMPLIFIER_ENTRY_IDS) {
    const charges = batch[id] ?? [];
    const positiveCharges = charges.filter(
      (charge) => Number.isFinite(charge.amount) && charge.amount > 0,
    );
    if (positiveCharges.length === 0) continue;
    const amount = positiveCharges.reduce(
      (sum, charge) => sum + charge.amount,
      0,
    );
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const sourceAmounts = positiveCharges.reduce(
      (acc, charge) => {
        acc[charge.sourceColorKey] =
          (acc[charge.sourceColorKey] ?? 0) + charge.amount;
        return acc;
      },
      {} as Record<HovAmplifierColorKey, number>,
    );
    const segment: HovAmplifierGaugeVisualSegment = {
      amount,
      sources: normalizeVisualSegmentSources(sourceAmounts),
    };
    next = {
      ...next,
      [id]: normalizeVisualSegments([segment, ...(next[id] ?? [])]),
    };
  }
  return next;
}

export interface HovAmplifierGaugeVisualSegmentsDrainResult {
  readonly segmentsByEntry: HovAmplifierGaugeVisualSegments;
  // Dominant color of the rightmost visual segment that was actually drained
  // during this call. `null` when nothing was drained. Use this as the
  // upstream visual source when propagating the drained color to a lower
  // amplifier's visual segments.
  readonly consumedVisualSource: HovAmplifierColorKey | null;
}

export function drainHovAmplifierGaugeVisualSegments(
  segmentsByEntry: HovAmplifierGaugeVisualSegments,
  entryId: HovAmplifierEntryId,
  amount: number,
): HovAmplifierGaugeVisualSegmentsDrainResult {
  if (!Number.isFinite(amount) || amount <= 0) {
    return { segmentsByEntry, consumedVisualSource: null };
  }
  const current = segmentsByEntry[entryId] ?? [];
  if (current.length === 0) {
    return { segmentsByEntry, consumedVisualSource: null };
  }

  const rightmostBefore = current[current.length - 1];
  const consumedVisualSource =
    rightmostBefore !== undefined
      ? resolveDominantVisualSegmentSource(rightmostBefore)
      : null;

  let remainingDrain = amount;
  const nextSegments = [...current];
  while (remainingDrain > 0 && nextSegments.length > 0) {
    const last = nextSegments[nextSegments.length - 1];
    if (last === undefined) break;
    if (last.amount > remainingDrain) {
      nextSegments[nextSegments.length - 1] = {
        ...last,
        amount: last.amount - remainingDrain,
      };
      remainingDrain = 0;
      break;
    }
    remainingDrain -= last.amount;
    nextSegments.pop();
  }

  return {
    segmentsByEntry: {
      ...segmentsByEntry,
      [entryId]: normalizeVisualSegments(nextSegments),
    },
    consumedVisualSource,
  };
}

export function syncHovAmplifierGaugeVisualSegmentsToGaugeValues(
  segmentsByEntry: HovAmplifierGaugeVisualSegments,
  state: HovAmplifierState,
): HovAmplifierGaugeVisualSegments {
  let next = segmentsByEntry;
  for (const entry of state.entries) {
    const gaugeValue = clamp01(entry.gaugeValue);
    const current = next[entry.id] ?? [];
    const total = sumVisualSegmentAmounts(current);
    if (gaugeValue <= 0) {
      if (current.length > 0) next = { ...next, [entry.id]: [] };
      continue;
    }
    if (current.length === 0) {
      next = {
        ...next,
        [entry.id]: [
          {
            amount: gaugeValue,
            sources: { [entry.id]: 1 },
          },
        ],
      };
      continue;
    }
    if (Math.abs(total - gaugeValue) <= HOV_AMPLIFIER_VISUAL_SEGMENT_EPSILON) {
      continue;
    }
    if (total > gaugeValue) {
      next = drainVisualSegmentsFromRight(next, entry.id, total - gaugeValue);
      continue;
    }
    next = addHovAmplifierGaugeVisualCharges(next, {
      [entry.id]: [{ amount: gaugeValue - total, sourceColorKey: entry.id }],
    });
  }
  return next;
}

export function resolveHovAmplifierDrainTargetGainFromGauge(
  actualGaugeDrain: number,
  targetGainPerGauge: HugeNumberInput,
): HugeNumber {
  if (!Number.isFinite(actualGaugeDrain) || actualGaugeDrain <= 0) {
    return hugeZero();
  }
  const perGaugeHuge = huge(targetGainPerGauge);
  if (!perGaugeHuge.isFinite() || compareHuge(perGaugeHuge, 0) <= 0) {
    return hugeZero();
  }

  return huge(actualGaugeDrain).mul(perGaugeHuge);
}

export function resolveHovAmplifierUpgradeCostForEntry(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): HugeNumber | null {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return null;
  const entry = findEntryState(state.entries, entryId);
  const level = normalizeEnhancementLevel(entry?.enhancementLevel);
  return resolveHovAmplifierUpgradeCost(definition.baseCost, level);
}

export function applyHovAmplifierEnhancementPurchase(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): HovAmplifierState {
  const entries = state.entries.map((entry) => {
    if (entry.id !== entryId) return entry;
    return {
      ...entry,
      enhancementLevel: normalizeEnhancementLevel(entry.enhancementLevel) + 1,
    };
  });
  return { ...state, entries };
}

export function resolveHovAmplifierEntryRemainingSeconds(
  gaugeValue: number,
  drainRatePerSecond: number,
): number | null {
  const normalizedGaugeValue = clamp01(gaugeValue);
  if (normalizedGaugeValue <= 0) return null;
  if (!Number.isFinite(drainRatePerSecond) || drainRatePerSecond <= 0) {
    return null;
  }

  return normalizedGaugeValue / drainRatePerSecond;
}

export function resolveHovAmplifierEntryIncomingChargeRatePerSecond(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): number {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return 0;
  const sourceId = definition.receivesGaugeFromEntryId;
  if (sourceId === null) return 0;
  if (!isHovAmplifierEntryUnlocked(state, sourceId)) return 0;
  const sourceEntry = state.entries.find((entry) => entry.id === sourceId);
  if (!sourceEntry) return 0;
  if (clamp01(sourceEntry.gaugeValue) <= 0) return 0;
  const sourceDefinition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === sourceId,
  );
  if (!sourceDefinition) return 0;
  const rate =
    sourceDefinition.baseDrainRatePerSecond *
    HOV_AMPLIFIER_LOWER_GAUGE_CHARGE_RATIO;
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return rate;
}

export function resolveHovAmplifierEntryNetDrainRatePerSecond(
  state: HovAmplifierState,
  entryId: HovAmplifierEntryId,
): number {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  if (!definition) return 0;
  const grossDrain = definition.baseDrainRatePerSecond;
  if (!Number.isFinite(grossDrain) || grossDrain <= 0) return 0;
  const incoming = resolveHovAmplifierEntryIncomingChargeRatePerSecond(
    state,
    entryId,
  );
  return grossDrain - incoming;
}

export function formatHovAmplifierRemainingTime(seconds: number): string {
  const totalSeconds =
    Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${pad2(minutes)}:${pad2(remainingSeconds)}`;
  }

  return `${pad2(minutes)}:${pad2(remainingSeconds)}`;
}

function createEntrySnapshot(
  definition: HovAmplifierEntryDefinition,
  entries: readonly HovAmplifierEntryState[],
  state: HovAmplifierState,
  availableHovCore: HugeNumber,
  recentChargeSources: HovAmplifierRecentChargeSources | undefined,
  recentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs | undefined,
  visualSegmentsByEntry: HovAmplifierGaugeVisualSegments | undefined,
  nowMs: number | undefined,
  amplifierGainMultipliersByEntry:
    | Partial<Record<HovAmplifierEntryId, HugeNumber>>
    | undefined,
  availability: HovAmplifierEntryAvailabilityContext | undefined,
  challengeState: HovAmplifierChallengeState,
): HovAmplifierEntrySnapshot {
  const entry = findEntryState(entries, definition.id);
  const gaugeValue = clamp01(entry?.gaugeValue ?? 0);
  const enhancementLevel = normalizeEnhancementLevel(entry?.enhancementLevel);
  const unlocked = definition.unlockedByDefault || enhancementLevel > 0;
  const fillRatePerSecond = definition.baseFillRatePerSecond;
  const drainRatePerSecond = definition.baseDrainRatePerSecond;
  const incomingChargeRatePerSecond = unlocked
    ? resolveHovAmplifierEntryIncomingChargeRatePerSecond(state, definition.id)
    : 0;
  const netDrainRatePerSecond = unlocked
    ? resolveHovAmplifierEntryNetDrainRatePerSecond(state, definition.id)
    : 0;
  const remainingSeconds = unlocked
    ? resolveHovAmplifierEntryRemainingSeconds(
        gaugeValue,
        netDrainRatePerSecond,
      )
    : null;
  const isDraining = unlocked && gaugeValue > 0 && drainRatePerSecond > 0;
  const isStarved = unlocked && gaugeValue <= 0 && drainRatePerSecond > 0;
  const isWeakBurn = false;

  const amplifierGainPerSecond =
    resolveHovAmplifierGainPerSecond(enhancementLevel);
  const amplifierGainPerSecondAfterUpgrade = resolveHovAmplifierGainPerSecond(
    enhancementLevel + 1,
  );
  const cumulativeBoost = resolveHovAmplifierCumulativeBoost(
    state,
    definition.id,
  );
  const amplifierGainMultiplier = resolveHovAmplifierGainMultiplier(
    state,
    definition.id,
  );
  const entryGainMultiplier = resolveHovAmplifierEntryGainMultiplier(
    amplifierGainMultipliersByEntry,
    definition.id,
  );
  const challengeSourceGainMultiplier =
    challengeState.sourceGainMultipliersByEntry[definition.id];
  const combinedSourceMultiplier = amplifierGainMultiplier
    .mul(entryGainMultiplier)
    .mul(challengeSourceGainMultiplier);
  const effectiveAmplifierGainPerSecond =
    resolveHovAmplifierEffectiveGainPerSecond(
      amplifierGainPerSecond,
      combinedSourceMultiplier,
    );
  const effectiveAmplifierGainPerSecondAfterUpgrade =
    resolveHovAmplifierEffectiveGainPerSecond(
      amplifierGainPerSecondAfterUpgrade,
      combinedSourceMultiplier,
    );
  const corruptedPresentation = challengeState.corruptedEntryIds.has(
    definition.id,
  );
  const activeGaugeOutputGain = resolveHovAmplifierActiveGaugeOutputGain({
    unlocked: unlocked && !corruptedPresentation,
    gaugeValue,
    drainRatePerSecond,
    effectiveAmplifierGainPerSecond,
  });
  const drainTarget = resolveHovAmplifierDrainTarget(definition.id);
  const gaugeOutputLabel = resolveHovAmplifierGaugeOutputLabel(drainTarget);
  const upgradeCost = resolveHovAmplifierUpgradeCost(
    definition.baseCost,
    enhancementLevel,
  );
  const playerVisible = isHovAmplifierEntryPlayerVisible(
    definition.id,
    availability,
  );
  const mpAssignable = isHovAmplifierEntryMpAssignable(
    definition.id,
    availability,
  );
  const canPurchaseUpgrade =
    isHovAmplifierEntryPurchasable(definition.id, availability) &&
    !corruptedPresentation &&
    compareHuge(availableHovCore, upgradeCost) >= 0;
  const missingHovCoreForUpgrade = canPurchaseUpgrade
    ? hugeZero()
    : maxHuge(hugeZero(), upgradeCost.sub(availableHovCore));
  const recentChargeSource =
    gaugeValue > 0
      ? resolveRecentChargeSource(definition.id, recentChargeSources)
      : null;
  const hasRecentDirectCharge =
    gaugeValue > 0 &&
    hasRecentDirectChargeWithin(
      definition.id,
      recentDirectChargeAtMs,
      nowMs,
      HOV_AMPLIFIER_RECENT_DIRECT_CHARGE_WINDOW_MS,
    );
  const recentChargeMode = resolveRecentChargeMode({
    hasDirect: hasRecentDirectCharge,
    hasSupport: recentChargeSource !== null,
  });
  const visualSegments = normalizeVisualSegments(
    visualSegmentsByEntry?.[definition.id] ?? [],
  );

  return {
    id: definition.id,
    index: definition.index,
    colorKey: definition.colorKey,
    labelKey: definition.labelKey,
    shortLabel: HOV_AMPLIFIER_SHORT_LABELS[definition.id],
    gainUpgradeShortLabel:
      HOV_AMPLIFIER_GAIN_UPGRADE_SHORT_LABELS[definition.id],
    feedsGaugeForEntryId: definition.feedsGaugeForEntryId,
    receivesGaugeFromEntryId: definition.receivesGaugeFromEntryId,
    recentChargeSource,
    recentChargeMode,
    visualSegments,
    consumingVisualSource: resolveConsumingVisualSource(visualSegments),
    playerVisible,
    corruptedPresentation,
    mpAssignable,
    unlocked,
    gaugeValue,
    fillRatePerSecond,
    drainRatePerSecond,
    incomingChargeRatePerSecond,
    netDrainRatePerSecond,
    remainingSeconds,
    isDraining,
    isStarved,
    isWeakBurn,
    burnState: resolveBurnState({ isDraining, isStarved, isWeakBurn }),
    enhancementLevel,
    amplifierGainPerSecond,
    amplifierGainPerSecondAfterUpgrade,
    amplifierGainMultiplier,
    challengeSourceGainMultiplier,
    cumulativeBoost,
    effectiveAmplifierGainPerSecond,
    effectiveAmplifierGainPerSecondAfterUpgrade,
    activeGaugeOutputGain,
    drainTarget,
    gaugeOutputLabel,
    upgradeCost,
    canPurchaseUpgrade,
    missingHovCoreForUpgrade,
  };
}

function resolveRecentChargeSource(
  chargedEntryId: HovAmplifierEntryId,
  recentChargeSources: HovAmplifierRecentChargeSources | undefined,
): HovAmplifierRecentChargeSource | null {
  const sourceEntryId = recentChargeSources?.[chargedEntryId] ?? null;
  if (sourceEntryId === null) return null;

  const sourceDefinition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === sourceEntryId,
  );
  if (sourceDefinition?.feedsGaugeForEntryId !== chargedEntryId) {
    return null;
  }

  return {
    sourceEntryId,
    chargedEntryId,
    sourceColorKey: resolveVisualChargeColor(
      sourceEntryId,
      sourceDefinition.colorKey,
      recentChargeSources,
    ),
  };
}

function hasRecentDirectChargeWithin(
  entryId: HovAmplifierEntryId,
  recentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs | undefined,
  nowMs: number | undefined,
  windowMs: number,
): boolean {
  if (recentDirectChargeAtMs === undefined) return false;
  if (typeof nowMs !== "number" || !Number.isFinite(nowMs)) return false;
  if (!Number.isFinite(windowMs) || windowMs < 0) return false;
  const lastAt = recentDirectChargeAtMs[entryId];
  if (typeof lastAt !== "number" || !Number.isFinite(lastAt)) return false;
  return nowMs - lastAt <= windowMs;
}

export function resolveRecentChargeMode(input: {
  readonly hasDirect: boolean;
  readonly hasSupport: boolean;
}): HovAmplifierRecentChargeMode | null {
  if (input.hasDirect && input.hasSupport) return "direct-and-support";
  if (input.hasSupport) return "support-only";
  if (input.hasDirect) return "direct-only";
  return null;
}

function resolveVisualChargeColor(
  entryId: HovAmplifierEntryId,
  ownColorKey: HovAmplifierColorKey,
  recentChargeSources: HovAmplifierRecentChargeSources | undefined,
): HovAmplifierColorKey {
  if (recentChargeSources === undefined) return ownColorKey;

  const visited = new Set<HovAmplifierEntryId>();
  let currentId: HovAmplifierEntryId = entryId;
  let currentColor: HovAmplifierColorKey = ownColorKey;
  while (!visited.has(currentId)) {
    visited.add(currentId);
    const upperId = recentChargeSources[currentId] ?? null;
    if (upperId === null) return currentColor;
    const upperDefinition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
      (candidate) => candidate.id === upperId,
    );
    if (
      !upperDefinition ||
      upperDefinition.feedsGaugeForEntryId !== currentId
    ) {
      return currentColor;
    }
    currentId = upperId;
    currentColor = upperDefinition.colorKey;
  }
  return currentColor;
}

export function resolveHovAmplifierVisualChargeColor(
  entryId: HovAmplifierEntryId,
  recentChargeSources: HovAmplifierRecentChargeSources | undefined,
): HovAmplifierColorKey {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === entryId,
  );
  return resolveVisualChargeColor(
    entryId,
    definition?.colorKey ?? entryId,
    recentChargeSources,
  );
}

function normalizeVisualSegments(
  segments: readonly HovAmplifierGaugeVisualSegment[],
): readonly HovAmplifierGaugeVisualSegment[] {
  const normalized: HovAmplifierGaugeVisualSegment[] = [];
  for (const segment of segments) {
    if (!Number.isFinite(segment.amount) || segment.amount <= 0) continue;
    const sources = normalizeVisualSegmentSources(segment.sources);
    if (Object.keys(sources).length === 0) continue;
    const nextSegment = { amount: segment.amount, sources };
    const previous = normalized[normalized.length - 1];
    if (previous && haveSameVisualSegmentSources(previous, nextSegment)) {
      normalized[normalized.length - 1] = {
        amount: previous.amount + nextSegment.amount,
        sources: previous.sources,
      };
      continue;
    }
    normalized.push(nextSegment);
  }

  while (normalized.length > HOV_AMPLIFIER_MAX_VISUAL_SEGMENTS_PER_ENTRY) {
    const first = normalized.shift();
    const second = normalized.shift();
    if (!first || !second) break;
    normalized.unshift(mergeVisualSegments(first, second));
  }

  return normalized;
}

function normalizeVisualSegmentSources(
  sources: Partial<Record<HovAmplifierColorKey, number>>,
): Partial<Record<HovAmplifierColorKey, number>> {
  const positiveSources = HOV_AMPLIFIER_ENTRY_IDS.map((id) => ({
    id,
    amount: sources[id] ?? 0,
  })).filter((source) => Number.isFinite(source.amount) && source.amount > 0);
  const total = positiveSources.reduce((sum, source) => sum + source.amount, 0);
  if (!Number.isFinite(total) || total <= 0) return {};

  return positiveSources.reduce(
    (acc, source) => {
      acc[source.id] = source.amount / total;
      return acc;
    },
    {} as Partial<Record<HovAmplifierColorKey, number>>,
  );
}

function haveSameVisualSegmentSources(
  left: HovAmplifierGaugeVisualSegment,
  right: HovAmplifierGaugeVisualSegment,
): boolean {
  for (const id of HOV_AMPLIFIER_ENTRY_IDS) {
    if (Math.abs((left.sources[id] ?? 0) - (right.sources[id] ?? 0)) > 0.0001) {
      return false;
    }
  }
  return true;
}

function mergeVisualSegments(
  left: HovAmplifierGaugeVisualSegment,
  right: HovAmplifierGaugeVisualSegment,
): HovAmplifierGaugeVisualSegment {
  const amount = left.amount + right.amount;
  if (amount <= 0) return left;
  const sources = HOV_AMPLIFIER_ENTRY_IDS.reduce(
    (acc, id) => {
      const sourceAmount =
        (left.sources[id] ?? 0) * left.amount +
        (right.sources[id] ?? 0) * right.amount;
      if (sourceAmount > 0) acc[id] = sourceAmount;
      return acc;
    },
    {} as Partial<Record<HovAmplifierColorKey, number>>,
  );
  return { amount, sources: normalizeVisualSegmentSources(sources) };
}

function sumVisualSegmentAmounts(
  segments: readonly HovAmplifierGaugeVisualSegment[],
): number {
  return segments.reduce((sum, segment) => sum + segment.amount, 0);
}

function drainVisualSegmentsFromRight(
  segmentsByEntry: HovAmplifierGaugeVisualSegments,
  entryId: HovAmplifierEntryId,
  amount: number,
): HovAmplifierGaugeVisualSegments {
  return drainHovAmplifierGaugeVisualSegments(segmentsByEntry, entryId, amount)
    .segmentsByEntry;
}

export function resolveDominantVisualSegmentSource(
  segment: HovAmplifierGaugeVisualSegment,
): HovAmplifierColorKey | null {
  let best: HovAmplifierColorKey | null = null;
  let bestRatio = 0;
  for (const id of HOV_AMPLIFIER_ENTRY_IDS) {
    const ratio = segment.sources[id] ?? 0;
    if (ratio > bestRatio) {
      best = id;
      bestRatio = ratio;
    }
  }
  return best;
}

function resolveConsumingVisualSource(
  segments: readonly HovAmplifierGaugeVisualSegment[],
): HovAmplifierColorKey | null {
  const rightmost = segments[segments.length - 1];
  if (rightmost === undefined) return null;
  return resolveDominantVisualSegmentSource(rightmost);
}

export function resolveHovAmplifierGaugeOutputLabel(
  drainTarget: HovAmplifierDrainTarget,
): string {
  if (drainTarget.kind === "ap") return "APG";
  return `${HOV_AMPLIFIER_GAIN_UPGRADE_SHORT_LABELS[drainTarget.entryId]}G`;
}

export interface HovAmplifierActiveGaugeOutputInput {
  readonly unlocked: boolean;
  readonly gaugeValue: number;
  readonly drainRatePerSecond: number;
  readonly effectiveAmplifierGainPerSecond: HugeNumber;
}

export function resolveHovAmplifierActiveGaugeOutputGain(
  input: HovAmplifierActiveGaugeOutputInput,
): HugeNumber {
  if (!input.unlocked) return hugeZero();
  if (!Number.isFinite(input.drainRatePerSecond)) return hugeZero();
  if (input.drainRatePerSecond <= 0) return hugeZero();
  if (!Number.isFinite(input.gaugeValue) || input.gaugeValue <= 0) {
    return hugeZero();
  }
  return input.effectiveAmplifierGainPerSecond;
}

function createInitialEntryState(
  id: HovAmplifierEntryId,
): HovAmplifierEntryState {
  const definition = HOV_AMPLIFIER_ENTRY_DEFINITIONS.find(
    (candidate) => candidate.id === id,
  );
  return {
    id,
    gaugeValue: 0,
    enhancementLevel: definition?.initialEnhancementLevel ?? 0,
  };
}

function findEntryState(
  entries: readonly HovAmplifierEntryState[],
  id: HovAmplifierEntryId,
): HovAmplifierEntryState | undefined {
  return entries.find((entry) => entry.id === id);
}

function resolveBurnState(options: {
  readonly isDraining: boolean;
  readonly isStarved: boolean;
  readonly isWeakBurn: boolean;
}): HovAmplifierBurnState {
  if (options.isStarved) return "starved";
  if (options.isWeakBurn) return "weak";
  if (options.isDraining) return "draining";
  return "idle";
}

function normalizeEnhancementLevel(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    return 0;
  }

  return value;
}

function normalizeHovAmplifierEntryState(
  definition: HovAmplifierEntryDefinition,
  source: Record<string, unknown> | undefined,
): HovAmplifierEntryState {
  if (source === undefined) {
    return createInitialEntryState(definition.id);
  }
  return {
    id: definition.id,
    gaugeValue: clamp01(
      typeof source.gaugeValue === "number" ? source.gaugeValue : 0,
    ),
    enhancementLevel: Math.max(
      normalizeEnhancementLevel(source.enhancementLevel),
      definition.initialEnhancementLevel,
    ),
  };
}

function normalizeHovAmplifierCumulativeBoostValue(value: unknown): HugeNumber {
  if (isHugeNumber(value) && value.isFinite() && compareHuge(value, 0) >= 0) {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return huge(value);
  }
  if (typeof value === "string") {
    const parsed = parseHugeInput(value);
    if (parsed !== null && parsed.isFinite() && compareHuge(parsed, 0) >= 0) {
      return parsed;
    }
  }
  return hugeZero();
}

function normalizeHovAmplifierCumulativeBoosts(
  value: unknown,
): Record<HovAmplifierEntryId, HugeNumber> {
  const source = isPlainRecord(value) ? value : {};
  return HOV_AMPLIFIER_ENTRY_IDS.reduce(
    (acc, id) => {
      acc[id] = normalizeHovAmplifierCumulativeBoostValue(source[id]);
      return acc;
    },
    {} as Record<HovAmplifierEntryId, HugeNumber>,
  );
}

function normalizeHovAmplifierAp(value: unknown): HugeNumber {
  if (isHugeNumber(value) && value.isFinite() && compareHuge(value, 0) >= 0) {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return huge(value);
  }
  if (typeof value === "string") {
    const parsed = parseHugeInput(value);
    if (parsed !== null && parsed.isFinite() && compareHuge(parsed, 0) >= 0) {
      return parsed;
    }
  }
  return hugeOne();
}

function normalizeHovAmplifierApExponent(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return value;
  }
  return HOV_AMPLIFIER_INITIAL_AP_EXPONENT;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}
